import React, { useState } from 'react';
import { formatCredits } from '../utils/credits';
import Avatar from './Avatar';

const getEventIcon = (eventType) => {
    switch (eventType) {
        case 'purchased':
            return { icon: 'fa-shopping-cart', color: 'success' };
        case 'correction_proposed':
            return { icon: 'fa-edit', color: 'warning' };
        case 'terms_accepted':
            return { icon: 'fa-check-circle', color: 'success' };
        case 'terms_declined':
            return { icon: 'fa-times-circle', color: 'danger' };
        case 'reverted':
            return { icon: 'fa-undo', color: 'danger' };
        case 'screening_date_amended':
            return { icon: 'fa-calendar', color: 'info' };
        case 'hire_period_expired':
            return { icon: 'fa-hourglass-end', color: 'secondary' };
        default:
            return { icon: 'fa-circle', color: 'secondary' };
    }
};

const getEventTitle = (eventType) => {
    switch (eventType) {
        case 'purchased':
            return 'Order Purchased';
        case 'correction_proposed':
            return 'Corrections Proposed by BAC';
        case 'terms_accepted':
            return 'Terms Accepted';
        case 'terms_declined':
            return 'Terms Declined';
        case 'reverted':
            return 'Order Reverted';
        case 'screening_date_amended':
            return 'Screening Date Amended';
        case 'hire_period_expired':
            return 'Hire Period Expired';
        default:
            return 'Event';
    }
};

const OrderHistory = ({ orderHistory, isBAC }) => {
    const [expandedIndex, setExpandedIndex] = useState(null);

    if (!orderHistory || orderHistory.length === 0) {
        return (
            <div className="text-white-50 text-center py-4">
                <span className="fas fa-history me-2" />
                No history available
            </div>
        );
    }

    const formatChange = (field, change) => {
        if (!change || (change.from === undefined && change.to === undefined)) {
            return null;
        }

        const formatValue = (value, field) => {
            if (value === null || value === undefined) return 'None';
            if (field === 'screeningDate') return new Date(value).toLocaleString();
            if (field === 'price' || field === 'downpayment') return `${formatCredits(value)} credits`;
            return value;
        };

        const fieldLabel = {
            screeningDate: 'Screening Date',
            price: 'Price',
            downpayment: 'Downpayment',
            hirePeriod: 'Hire Period',
            hirePeriodType: 'Hire Period Type',
            status: 'Status'
        }[field] || field;

        return (
            <div key={field} className="mb-2">
                <strong className="text-primary">{fieldLabel}:</strong>{' '}
                <span className="text-white-50">{formatValue(change.from, field)}</span>
                <span className="fas fa-arrow-right mx-2 text-primary" />
                <span className="text-white">{formatValue(change.to, field)}</span>
            </div>
        );
    };

    const sorted = [...orderHistory].reverse();

    const toggleExpand = (index) => {
        setExpandedIndex(prev => prev === index ? null : index);
    };

    return (
        <div className="order-history">
            {sorted.map((entry, index) => {
                const { icon, color } = getEventIcon(entry.eventType);
                const eventTitle = getEventTitle(entry.eventType);
                const isExpanded = expandedIndex === index;
                const hasDetails = (entry.changes && Object.keys(entry.changes).length > 0)
                    || (entry.correction && isBAC)
                    || entry.transaction
                    || entry.notes;

                return (
                    <div key={index} className={`bg-broken border-start border-${color} border-3 mb-2 shadow-sm overflow-hidden`}>
                        <div
                            className={`d-flex align-items-center p-3 gap-3 ${hasDetails ? 'pointer' : ''}`}
                            onClick={() => hasDetails && toggleExpand(index)}
                            style={{ userSelect: 'none' }}
                        >
                            <div className={`bg-${color} rounded-circle d-flex align-items-center justify-content-center shadow-sm flex-shrink-0`} style={{ width: '36px', height: '36px' }}>
                                <span className={`fas ${icon} text-white`} style={{ fontSize: '0.8rem' }} />
                            </div>

                            <div className="flex-grow-1 min-w-0">
                                <div className="text-white fw-semibold">{eventTitle}</div>
                                <div className="text-white-50 small">{new Date(entry.timestamp).toLocaleString()}</div>
                            </div>

                            {entry.performedBy && (
                                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                    <Avatar
                                        imageUrl={entry.performedBy.profilePic?.imageUrl}
                                        name={`${entry.performedBy.firstName} ${entry.performedBy.lastName}`}
                                        size={28}
                                    />
                                    <div className="d-none d-md-block text-end">
                                        <div className="text-white small">{entry.performedBy.firstName} {entry.performedBy.lastName}</div>
                                        <div className="text-white-50" style={{ fontSize: '0.7rem' }}>{entry.performedBy.role}</div>
                                    </div>
                                </div>
                            )}

                            {hasDetails && (
                                <span className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} text-white-50 flex-shrink-0`} style={{ fontSize: '0.75rem' }} />
                            )}
                        </div>

                        {isExpanded && hasDetails && (
                            <div className="px-3 pb-3 border-top border-white/10">
                                {entry.changes && Object.keys(entry.changes).length > 0 && (
                                    <div className="bg-black/50 p-3 rounded mt-3">
                                        <div className="text-white-50 small mb-2">
                                            <span className="fas fa-list-ul me-2" />
                                            Changes
                                        </div>
                                        {Object.entries(entry.changes).map(([field, change]) => formatChange(field, change))}
                                    </div>
                                )}

                                {entry.correction && isBAC && (
                                    <div className="bg-warning/10 border border-warning/25 p-3 rounded mt-3">
                                        <div className="text-warning small mb-2">
                                            <span className="fas fa-edit me-2" />
                                            Proposed Corrections
                                        </div>
                                        {entry.correction.screeningDate && (
                                            <div className="mb-2">
                                                <strong className="text-primary">Screening Date:</strong>{' '}
                                                <span className="text-white">{new Date(entry.correction.screeningDate).toLocaleString()}</span>
                                            </div>
                                        )}
                                        {entry.correction.hirePeriod && (
                                            <div className="mb-2">
                                                <strong className="text-primary">Hire Period:</strong>{' '}
                                                <span className="text-white">{entry.correction.hirePeriod} {entry.correction.hirePeriodType}</span>
                                            </div>
                                        )}
                                        {entry.correction.priceCorrection !== undefined && entry.correction.priceCorrection !== null && (
                                            <div className="mb-2">
                                                <strong className="text-primary">Price Correction:</strong>{' '}
                                                <span className={entry.correction.priceCorrection > 0 ? 'text-danger' : 'text-success'}>
                                                    {entry.correction.priceCorrection > 0 ? '+' : ''}{formatCredits(entry.correction.priceCorrection)} credits
                                                </span>
                                            </div>
                                        )}
                                        {entry.correction.terms && (
                                            <div className="mb-0">
                                                <strong className="text-primary">Terms:</strong>{' '}
                                                <div className="text-white small mt-1 fst-italic">"{entry.correction.terms}"</div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {entry.transaction && (
                                    <div className="bg-black/50 p-2 rounded mt-3">
                                        <div className="text-white-50 small">
                                            <span className="fas fa-receipt me-2" />
                                            Transaction: {entry.transaction.description}
                                        </div>
                                    </div>
                                )}

                                {entry.notes && (
                                    <div className="bg-black/50 p-3 rounded mt-3">
                                        <div className="text-white-50 small mb-1">
                                            <span className="fas fa-sticky-note me-2" />
                                            Notes
                                        </div>
                                        <div className="text-white small">{entry.notes}</div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default OrderHistory;
